import { type RefObject, useEffect, useRef, useState } from 'react';

import type { ContinueOutcome } from './step-footer.tsx';

/**
 * Continue's `run` for a step whose write is a confirm or an approval, and the refusal it takes
 * the reader to. The footer is pinned far from the step's own error line, so a refusal said only
 * there could go unseen: the line it is written on takes the focus and is scrolled to the middle
 * of the window.
 *
 * The step puts `refusal` on every line a refused write can be said on (with `tabIndex={-1}`), and
 * registers `continueRun(write)`, where `write` makes the step's confirm or approval and resolves
 * whether the server accepted it. The focus moves after the commit that follows a refusal, so a
 * line that renders with the refusal is there to take it.
 */
export const useRefusalFocus = (): {
  refusal: RefObject<HTMLParagraphElement | null>;
  continueRun: (write: () => Promise<boolean>) => () => Promise<ContinueOutcome>;
} => {
  const refusal = useRef<HTMLParagraphElement | null>(null);
  const [asked, setAsked] = useState(0);

  // Nothing is on the line before the first refusal, so the run on mount finds nothing to move to.
  useEffect(() => {
    const line = refusal.current;
    line?.focus();
    // jsdom draws nothing, and has no scrolling to do.
    if (typeof line?.scrollIntoView === 'function') {
      line.scrollIntoView({ block: 'center' });
    }
  }, [asked]);

  return {
    refusal,
    continueRun: (write) => async () => {
      if (await write()) {
        return 'advance';
      }

      setAsked((times) => times + 1);
      return 'stay';
    },
  };
};
